/*
 * <klaviyo-form> - themed email capture that submits to Klaviyo's browser-safe
 * Client Subscriptions API, keeping the user on-page and toggling an inline
 * success / error state. Falls back to the native form post when JS is off or
 * the company / list id isn't configured.
 *
 * Shared by sections/newsletter.liquid and sections/footer.liquid. Expects:
 *   data-endpoint     Client API base, e.g. https://a.klaviyo.com/client/subscriptions/
 *   data-list-id      Klaviyo list id (the form/list to subscribe to)
 *   data-account-id   Klaviyo public API key (company id)
 * and child hooks: [data-klaviyo-form-el] [data-klaviyo-email] [data-klaviyo-submit]
 *   [data-klaviyo-error] [data-klaviyo-success]
 */
if (!customElements.get('klaviyo-form')) {
  class KlaviyoForm extends HTMLElement {
    // Resolved on use, and the submit is delegated on this element: a footer or
    // newsletter block replaced by an A/B swap keeps working without re-binding,
    // and the refs can never point at markup that has been thrown away.
    get form() { return this.querySelector('[data-klaviyo-form-el]'); }
    get emailEl() { return this.querySelector('[data-klaviyo-email]'); }
    get submitEl() { return this.querySelector('[data-klaviyo-submit]'); }
    get errorEl() { return this.querySelector('[data-klaviyo-error]'); }
    get successEl() { return this.querySelector('[data-klaviyo-success]'); }
    get endpoint() { return this.dataset.endpoint; }
    get listId() { return this.dataset.listId; }
    get companyId() { return this.dataset.accountId; }

    connectedCallback() {
      // connectedCallback also fires on a plain move; the listener lives on this
      // element and survives it, so bind it only once.
      if (this._bound) return;
      this._bound = true;
      ThemeBind.delegate(this, 'submit', '[data-klaviyo-form-el]', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      // No company/list id configured: let the native form post happen.
      if (!this.companyId || !this.listId) return;
      event.preventDefault();

      const emailEl = this.emailEl;
      const email = ((emailEl && emailEl.value) || '').trim();
      if (!email) return;

      this.setError('');
      const submitEl = this.submitEl;
      if (submitEl) submitEl.disabled = true;

      const url = this.endpoint + '?company_id=' + encodeURIComponent(this.companyId);
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } },
        },
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify(payload),
        });
        // The Client API returns 202 with an empty body on success.
        if (res.ok) {
          const form = this.form;
          if (form) form.classList.add('hidden');
          const successEl = this.successEl;
          if (successEl) successEl.classList.remove('hidden');
          return;
        }
        let message = '';
        try {
          const data = await res.json();
          if (data && data.errors && data.errors.length) message = data.errors[0].detail || data.errors[0].title;
        } catch (e) {}
        this.setError(message || 'Something went wrong. Please try again.');
      } catch (e) {
        this.setError('Something went wrong. Please try again.');
      } finally {
        // Re-resolved: the block can be replaced while the request is in flight.
        const current = this.submitEl;
        if (current) current.disabled = false;
      }
    }

    setError(message) {
      const errorEl = this.errorEl;
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.toggle('hidden', !message);
    }
  }
  customElements.define('klaviyo-form', KlaviyoForm);
}
